
import { Student } from "@/services/db/types";
import { format } from "date-fns";

/**
 * Generate the HTML content for student certificate
 */
export const generateCertificateContent = (
  student: Student, 
  programId?: string, 
  instructorName?: string, 
  directorName?: string,
  grade?: string,
  percentage?: string
): string => {
  // Get full student name including surname
  const fullName = student.surname ? `${student.name} ${student.surname}` : student.name;
  
  // Find the specific program or use the first completed one
  let selectedProgram = null;
  if (programId) {
    selectedProgram = student.programs?.find(p => p.title === programId);
  } else {
    selectedProgram = student.programs?.find(p => p.feeStatus === 'paid') || student.programs?.[0];
  }

  if (!selectedProgram) {
    throw new Error('No eligible program found for certificate');
  }

  // Use provided names or default values
  const finalInstructorName = instructorName || 'Dr. Ahmad Ali';
  const finalDirectorName = directorName || 'Prof. Mohammad Hassan';

  // Use provided grade or fallback to default only if no grade is provided
  const finalGrade = grade || 'Excellent';
  
  // Format the grade display with percentage if provided
  let gradeDisplay = finalGrade;
  if (percentage && percentage.trim() !== '') {
    gradeDisplay = `${finalGrade} (${percentage}%)`;
  }

  // Get the CSS class for the grade color
  const getGradeColorClass = (gradeValue: string) => {
    const lowerGrade = gradeValue.toLowerCase();
    if (lowerGrade === 'excellent') return 'grade-excellent';
    if (lowerGrade === 'good') return 'grade-good';
    if (lowerGrade === 'poor') return 'grade-poor';
    if (lowerGrade === 'failed') return 'grade-failed';
    return 'grade-excellent'; // default fallback
  };

  const gradeColorClass = getGradeColorClass(finalGrade);

  // Console log to debug the values being used
  console.log('Certificate generation - Grade:', finalGrade, 'Percentage:', percentage, 'Display:', gradeDisplay, 'CSS Class:', gradeColorClass);

  return `
    <div class="certificate-page">
      <div class="certificate-border">
        <!-- Header with centered institution name -->
        <div class="certificate-header-inside">
          <div class="header-content-centered">
            <div class="institution-name-main">Noor Ali Learning Center</div>
            <div class="certificate-subtitle-header">Certificate of Completion</div>
          </div>
        </div>
        
        <div class="certificate-main">
          <div class="generation-date">Generated on ${format(new Date(), 'PPPP')}</div>
          
          <div class="certificate-title">CERTIFICATE OF COMPLETION</div>
          <div class="certificate-subtitle-main">This is to certify that</div>
          
          <div class="student-info-section">
            <div class="student-name-certificate">${fullName}</div>
            <div class="student-father-name">Son of ${student.fathersName || 'N/A'}</div>
            <div class="student-details">Student ID: ${student.id}</div>
          </div>
          
          <div class="completion-text">
            has successfully completed the course of study in
          </div>
          
          <div class="program-details">
            <div class="program-title-cert">${selectedProgram.title}</div>
            <div class="program-category-cert">Category: ${selectedProgram.category}</div>
          </div>
          
          <div class="program-info-grid">
            <div class="info-group">
              <div class="info-item">
                <span class="info-label">Duration:</span>
                <span class="info-value">${selectedProgram.duration || 'N/A'} days</span>
              </div>
              <div class="info-item">
                <span class="info-label">Completion Date:</span>
                <span class="info-value">${selectedProgram.completionDate ? format(new Date(selectedProgram.completionDate), 'PPPP') : format(new Date(), 'PPPP')}</span>
              </div>
            </div>
            <div class="info-group">
              <div class="info-item">
                <span class="info-label">Enrolled Date:</span>
                <span class="info-value">${selectedProgram.enrolledAt ? format(new Date(selectedProgram.enrolledAt), 'PPPP') : 'N/A'}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Grade:</span>
                <span class="info-value ${gradeColorClass}">${gradeDisplay}</span>
              </div>
            </div>
          </div>
          
          <div class="certificate-message">
            This certificate is awarded in recognition of successful completion of the above-mentioned program 
            and demonstrates proficiency in the subject matter as determined by the faculty of Noor Ali Learning Center.
          </div>
          
          <div class="signature-section">
            <div class="signature-item">
              <div class="signature-line"></div>
              <div class="signature-label">Instructor Signature</div>
              <div class="signature-name">${finalInstructorName}</div>
            </div>
            <div class="signature-item">
              <div class="signature-line"></div>
              <div class="signature-label">Director Signature</div>
              <div class="signature-name">${finalDirectorName}</div>
            </div>
          </div>
          
          <div class="certificate-number-container">
            <div class="certificate-number">
              Certificate No: NALC-${student.id}-${selectedProgram.title.replace(/\s+/g, '-')}-${new Date().getFullYear()}
            </div>
          </div>
        </div>
        
        <!-- Footer with institution details -->
        <div class="certificate-footer-inside">
          <div class="footer-content">
            <div class="institution-address">
              Noor Ali Learning Center | Address: 3rd Zone, Andishen 14th Street | Tel: 0784350000 | Email: nalcpolicehead.office@gmail.com
            </div>
            <div class="footer-note">
              <strong>NOTE:</strong> Without stamp the slip is invalid.
            </div>
            <div class="footer-confidential">
              This document is confidential and intended for educational purposes only. | © 2025 Noor Ali Learning Center, All rights reserved.
            </div>
          </div>
        </div>
      </div>
    </div>
  `;
};

/**
 * Generate filename for certificate
 */
export const generateCertificateFilename = (student: Student): string => {
  const fullName = student.surname ? `${student.name} ${student.surname}` : student.name;
  return `Student_Certificate_${fullName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}`;
};
